import { useCallback } from 'react';
import { useAnalytics } from '@/services/analytics';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { activityTabName, specificActivityViewedProps } from '@/features/activity-feed';
import type { ActivityLogRow, ActivityLogTab } from '@/features/activity-feed';

/**
 * The page's three analytics events, ported from the Ember logs page (FPS-2304): a page view per
 * tab, plus callbacks for the page to fire on "Load more" and on row expansion.
 */
export function useActivityLogsAnalytics(tab: ActivityLogTab, rows: ActivityLogRow[]) {
  const { track } = useAnalytics();
  const workspace = useCurrentWorkspace();
  const tabName = activityTabName(tab);

  usePageViewTracking(
    useCallback(() => {
      track('logs', 'viewed', { 'Activity Tab': tabName });
    }, [track, tabName]),
    // `track` drops the event outright while the workspace context is unresolved, and a page view
    // is only ever attempted once per location — so gate on the very condition `track` checks.
    // Gating on the route's workspace id instead isn't equivalent: for a numeric route param that
    // id is available immediately, so a cold load would mark the view tracked before the context
    // arrived and lose it for good.
    workspace != null,
    // The tab is a path segment but the filters are search params, so keying on the pathname alone
    // makes a tab switch a new page view while a filter change isn't one.
    { ignoreSearch: true },
  );

  const trackMoreLogsLoaded = useCallback(() => {
    track('logs', 'moreLogsLoaded', { 'Activity Tab': tabName });
  }, [track, tabName]);

  const trackRowsExpanded = useCallback(
    (rowIds: string[]) => {
      for (const id of rowIds) {
        const row = rows.find((candidate) => candidate.log.id === id);
        if (row != null) track('logs', 'specificActivityViewed', specificActivityViewedProps(tab, row.log));
      }
    },
    [rows, track, tab],
  );

  return { trackMoreLogsLoaded, trackRowsExpanded };
}
